import React, { useState } from 'react';
import { Clock, MapPin, X } from 'lucide-react';

const ACCENT = {
  doctor: '#F15A22',
  staff_nurse: '#0D9488',
  multi_skill_assistant: '#475569',
  lab_technician: '#4F46E5',
};

/** Short profile panel for a selected on-duty person. */
export default function OnDutyProfileCard({ person, onClose }) {
  const [failed, setFailed] = useState(false);
  if (!person) return null;
  const accent = ACCENT[person.role] || '#071B34';
  const initial = (person.name || '?').replace(/^Dr\.?\s*/i, '')[0]?.toUpperCase() || '?';

  return (
    <div
      className="rounded-2xl border bg-white shadow-lg p-4 sm:p-5 relative"
      data-testid="on-duty-profile-card"
      style={{ borderTopWidth: 3, borderTopColor: accent }}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 h-7 w-7 rounded-full bg-zinc-100 text-zinc-500 hover:bg-zinc-200 grid place-items-center"
        aria-label="Close"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex gap-4">
        <div className="h-20 w-20 rounded-2xl overflow-hidden ring-2 ring-white shadow shrink-0 bg-zinc-100">
          {person.photo_url && !failed ? (
            <img
              src={person.photo_url}
              alt={person.name}
              className="h-full w-full object-cover object-top"
              onError={() => setFailed(true)}
            />
          ) : (
            <div
              className="h-full w-full flex items-center justify-center text-2xl font-bold text-white"
              style={{ background: accent }}
            >
              {initial}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 pr-6">
          <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: accent }}>
            {person.role_label}
          </p>
          <h4 className="text-lg font-bold text-zinc-900 truncate" style={{ fontFamily: 'Manrope' }}>
            {person.title ? `${person.title} ` : ''}{person.name}
          </h4>
          {person.specialisation && (
            <p className="text-sm text-zinc-600 mt-0.5">{person.specialisation}</p>
          )}
          {person.qualifications && (
            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{person.qualifications}</p>
          )}
          {person.designation && (
            <p className="text-[11px] text-zinc-500 mt-1">{person.designation}</p>
          )}
        </div>
      </div>
      <div className="mt-4 space-y-1.5">
        {(person.shifts || []).map((s, i) => (
          <div
            key={`${s.start}-${i}`}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-700 rounded-lg bg-zinc-50 border px-3 py-2"
          >
            <span className="inline-flex items-center gap-1 font-semibold">
              <Clock className="h-3.5 w-3.5" style={{ color: accent }} />
              {s.start}–{s.end}
            </span>
            {s.location ? (
              <span className="inline-flex items-center gap-1 text-zinc-500">
                <MapPin className="h-3.5 w-3.5" /> {s.location}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
